import React, { useEffect, useState, useMemo, useCallback } from 'react';
import '../../styles/onboardingFlow/onboarding/askTara.scss';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  setAiOnboardingInprogress,
  setAiOnboardingStepCompleted,
  setAiOnboardingUserPrompt,
} from '../../store/reducers/userSlice';
import {
  setIsChatAnimTriggered,
  setIsSidebarMinimized,
} from '../../store/reducers/uiSlice';
import {
  setCurrentChat,
  setAllChatsHistory,
} from '../../store/reducers/chatSlice';
import generateRandomString from '../../lib/helper-functions/generateRandomString';
import get_all_connected_mcp_api_call from '../../lib/api/mcp/get-all-connected-mcp';
import update_all_shop_stats_api_call from '../../lib/api/dashboard/update_all_shop_stats_api_call';
import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';
import IntegrationQuestionSection from '../onboardingComponents/IntegrationQuestionSection';
import ChatIcon from '../../assets/onboarding/common/ChatCircle.svg';
import OnboardingLogoPanel from '../onboardingComponents/OnboardingLogoPanel';
import OnboardingFooter from '../onboardingComponents/OnboardingFooter';

// Sample prompts data - same as TestOnboarding1Questions
type PromptDef = {
  platform_name: string;
  type: 'A' | 'B' | 'C';
  prompt: string;
};

const samplePromptsData: PromptDef[] = [
  {
    platform_name: 'Shopify',
    type: 'A',
    prompt:
      'How many orders were placed in the past 7 days on my Shopify store?',
  },
  {
    platform_name: 'Shopify',
    type: 'B',
    prompt:
      'Compare the total revenue and number of orders on Shopify between this month and last month.',
  },
  {
    platform_name: 'Shopify',
    type: 'C',
    prompt:
      'Analyze the relationship between product categories and customer acquisition cost over the past 3 months on Shopify. Create a graph showing which categories have the highest ROI and suggest optimization strategies for underperforming categories.',
  },
  {
    platform_name: 'Stripe',
    type: 'A',
    prompt: 'What was the total payment volume on Stripe yesterday?',
  },
  {
    platform_name: 'Stripe',
    type: 'B',
    prompt:
      'Show me the failed payment rate and successful payment volume on Stripe for the past 30 days.',
  },
  {
    platform_name: 'Stripe',
    type: 'C',
    prompt:
      'Generate a comprehensive Stripe payment analytics report with graphs showing payment success rates by country, payment method performance, and churn analysis. Provide recommendations to reduce payment failures and increase revenue retention.',
  },
  {
    platform_name: 'Intercom',
    type: 'A',
    prompt:
      'How many new conversations were started on Intercom in the past 24 hours?',
  },
  {
    platform_name: 'Intercom',
    type: 'B',
    prompt:
      'What is the average response time and total number of resolved tickets on Intercom in the past 7 days?',
  },
  {
    platform_name: 'Intercom',
    type: 'C',
    prompt:
      'Create an Intercom customer support performance dashboard with graphs showing conversation volume trends, resolution time by category, and customer satisfaction scores. Analyze patterns and suggest improvements for support team efficiency.',
  },
  {
    platform_name: 'Hubspot',
    type: 'A',
    prompt: 'How many new companies were added to HubSpot in the past 7 days?',
  },
  {
    platform_name: 'Hubspot',
    type: 'B',
    prompt:
      'Compare the number of deals closed won versus deals closed lost in HubSpot in the past 30 days.',
  },
  {
    platform_name: 'Hubspot',
    type: 'C',
    prompt:
      'Analyze the HubSpot sales pipeline performance by creating graphs that show deal progression rates, average deal size by company type, and conversion rates at each stage. Provide insights on pipeline optimization and revenue forecasting recommendations.',
  },
  {
    platform_name: 'WooCommerce',
    type: 'A',
    prompt: 'What were the total sales on WooCommerce yesterday?',
  },
  {
    platform_name: 'WooCommerce',
    type: 'B',
    prompt:
      'Show me the top 5 selling products and their total quantities sold on WooCommerce in the past 30 days.',
  },
  {
    platform_name: 'WooCommerce',
    type: 'C',
    prompt:
      'Create a comprehensive WooCommerce e-commerce performance report with graphs showing sales trends, customer lifetime value analysis, and product performance metrics. Identify seasonal patterns and recommend inventory and marketing strategies for the next quarter.',
  },
  {
    platform_name: 'Google Analytics',
    type: 'A',
    prompt:
      'How many total page views did the website receive in Google Analytics yesterday, and which pages had the highest traffic?',
  },
  {
    platform_name: 'Google Analytics',
    type: 'B',
    prompt:
      'Compare the bounce rate and average session duration between organic and paid traffic sources in the past 30 days.',
  },
  {
    platform_name: 'Google Analytics',
    type: 'C',
    prompt:
      'Generate a complete performance summary of the website, showing key metrics such as user behavior, session duration, and traffic source effectiveness.',
  },
  {
    platform_name: 'Google Ads',
    type: 'A',
    prompt: 'What was the total ad spend on Google Ads in the past 7 days?',
  },
  {
    platform_name: 'Google Ads',
    type: 'B',
    prompt:
      'Show me the click-through rate and cost per click for all active Google Ads campaigns in the past 30 days.',
  },
  {
    platform_name: 'Google Ads',
    type: 'C',
    prompt:
      'Create a detailed Google Ads performance dashboard with graphs showing ROI by campaign, keyword performance analysis, and audience conversion rates. Analyze trends and provide optimization recommendations to improve ad spend efficiency and reduce cost per acquisition.',
  },
  {
    platform_name: 'Google Search Console',
    type: 'A',
    prompt:
      'How many total clicks did the website receive from Google Search Console in the past 7 days?',
  },
  {
    platform_name: 'Google Search Console',
    type: 'B',
    prompt:
      'Compare the average position and click-through rate for the top 10 performing keywords in Google Search Console in the past 30 days.',
  },
  {
    platform_name: 'Google Search Console',
    type: 'C',
    prompt:
      'Analyze Google Search Console SEO performance with graphs showing organic traffic trends, keyword ranking improvements, and page performance metrics. Identify content gaps and provide strategic recommendations for improving search visibility and organic growth.',
  },
  {
    platform_name: 'Facebook Ads',
    type: 'A',
    prompt: 'What was the total reach of all Facebook Ads campaigns yesterday?',
  },
  {
    platform_name: 'Facebook Ads',
    type: 'B',
    prompt:
      'Compare the cost per result and return on ad spend across all active Facebook Ads campaigns in the past 30 days.',
  },
  {
    platform_name: 'Facebook Ads',
    type: 'C',
    prompt:
      'Generate a comprehensive Facebook Ads advertising report with graphs showing audience engagement patterns, creative performance analysis, and conversion tracking across demographics. Provide recommendations for audience optimization and creative strategy improvements.',
  },
  {
    platform_name: 'TikTok Ads',
    type: 'A',
    prompt: 'How many impressions did TikTok Ads receive in the past 24 hours?',
  },
  {
    platform_name: 'TikTok Ads',
    type: 'B',
    prompt:
      'Show me the video view rate and cost per thousand impressions for all TikTok Ads campaigns in the past 7 days.',
  },
  {
    platform_name: 'TikTok Ads',
    type: 'C',
    prompt:
      'Create a TikTok Ads advertising performance analysis with graphs showing engagement rates by content type, audience behavior patterns, and campaign ROI metrics. Analyze viral potential and suggest content strategies to maximize reach and conversions.',
  },
  {
    platform_name: 'LinkedIn',
    type: 'A',
    prompt: 'How many clicks did LinkedIn Ads generate in the past 7 days?',
  },
  {
    platform_name: 'LinkedIn',
    type: 'B',
    prompt:
      'Compare the engagement rate and lead generation cost across all LinkedIn Ads campaign objectives in the past 30 days.',
  },
  {
    platform_name: 'LinkedIn',
    type: 'C',
    prompt:
      'Develop a comprehensive LinkedIn Ads insights report with graphs showing professional targeting effectiveness, B2B conversion analysis, and industry-specific performance metrics. Provide strategic recommendations for improving professional audience engagement and lead quality.',
  },
];

type GroupedPrompts = {
  platformName: string;
  logo?: string;
  prompts: PromptDef[];
};

const AskTara: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const allChatsHistory = useSelector(
    (state: RootState) => state.chat.allChatsHistory
  );
  const [connectedMCPs, setConnectedMCPs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set()
  );

  // Fetch connected MCPs
  useEffect(() => {
    const fetchConnectedMCPs = async () => {
      try {
        setLoading(true);
        const mcpData = await get_all_connected_mcp_api_call();
        if (mcpData && Array.isArray(mcpData)) {
          setConnectedMCPs(mcpData);
        } else {
          setConnectedMCPs([]);
        }
      } catch (error) {
        console.error('Error fetching connected MCPs:', error);
        setConnectedMCPs([]);
      } finally {
        setLoading(false);
      }
    };

    fetchConnectedMCPs();
  }, []);

  // Build quick lookup by platform name (case-insensitive) -> { name, logo }
  // Only include MCPs that are both active and valid (same logic as OnboardingIntegration)
  const byName = useMemo(() => {
    const map = new Map<string, { name: string; logo?: string }>();
    (connectedMCPs || []).forEach((c) => {
      const isActive = c?.is_active === true;
      const isValid = c?.is_valid !== false;
      if (!isActive || !isValid) return;
      const name: string | undefined = c?.tara_mcp?.mcp_name;
      const logo: string | undefined = c?.tara_mcp?.logo;
      if (name) map.set(name.toLowerCase(), { name, logo });
    });
    return map;
  }, [connectedMCPs]);

  // Group prompts by platform for connected integrations
  const groupedPrompts = useMemo(() => {
    const groups: Map<string, GroupedPrompts> = new Map();

    samplePromptsData.forEach((prompt) => {
      const platformLower = prompt.platform_name.toLowerCase();
      const entry = byName.get(platformLower);

      if (entry) {
        // Only include prompts for connected platforms
        if (!groups.has(platformLower)) {
          groups.set(platformLower, {
            platformName: entry.name,
            logo: entry.logo,
            prompts: [],
          });
        }
        groups.get(platformLower)!.prompts.push(prompt);
      }
    });

    // Convert to array and sort by platform name
    return Array.from(groups.values()).sort((a, b) =>
      a.platformName.localeCompare(b.platformName)
    );
  }, [byName]);

  // Expand first section by default if available
  useEffect(() => {
    if (groupedPrompts.length > 0 && expandedSections.size === 0) {
      setExpandedSections(new Set([groupedPrompts[0].platformName]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupedPrompts]);

  // Handle question selection and submission
  const handleQuestionSelect = useCallback(
    async (prompt: PromptDef) => {
      if (isSubmitting) return;

      const key = `${prompt.platform_name}-${prompt.type}`;
      setSelectedKey(key);
      setIsSubmitting(true);

      try {
        const ok = await update_all_shop_stats_api_call(5, true);
        if (ok) {
          dispatch(setAiOnboardingUserPrompt(prompt.prompt));
          dispatch(
            setAiOnboardingStepCompleted({ stepNumber: 5, isCompleted: true })
          );
          dispatch(setAiOnboardingInprogress(false));
          dispatch(setIsChatAnimTriggered(true));
          dispatch(setIsSidebarMinimized(true));

          // Create chat and navigate directly to chat page to avoid route
          // transition timing issues that hide the streaming steps UI
          const tempChatId = `temp_${generateRandomString(10)}`;
          const messageObj = {
            id: tempChatId,
            message: prompt.prompt,
            messageType: 'text' as const,
            debugInfo: null,
            role: 'user' as const,
          };
          const firstChat = {
            chatId: tempChatId,
            chatStartedDate: new Date().toISOString(),
            chatLastUpdatedDate: new Date().toISOString(),
            chatMessages: [messageObj],
          };
          dispatch(setCurrentChat(firstChat));
          dispatch(
            setAllChatsHistory([
              ...allChatsHistory,
              {
                chatId: firstChat.chatId,
                chatStartedDate: firstChat.chatStartedDate,
                chatLastUpdatedDate: firstChat.chatLastUpdatedDate,
                chatTitle: messageObj.message || 'Loading...',
                chatDescription: '',
              },
            ])
          );
          navigate(`/tara/chat/${tempChatId}`);
        }
      } catch (error) {
        console.error('Error submitting question:', error);
      } finally {
        setIsSubmitting(false);
      }
    },
    [dispatch, navigate, isSubmitting, allChatsHistory]
  );

  // Toggle section expand/collapse
  const toggleSection = (platformName: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(platformName)) {
        next.delete(platformName);
      } else {
        next.add(platformName);
      }
      return next;
    });
  };

  if (loading) {
    return (
      <div className='askTara'>
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className='askTara'>
      <OnboardingLogoPanel />
      <div className='askTara__header'>
        <div className='askTara__header-row'>
          <div className='askTara__header-icon'>
            <img src={ChatIcon} alt='Ask TARA' />
          </div>
          <h1 className='askTara__title'>
            {t('TestOnboarding1QuestionTitle')}
          </h1>
        </div>
        <p className='askTara__subtitle'>
          {t('TestOnboarding1QuestionSubtitle')}
        </p>
      </div>

      <div className='askTara__content'>
        {groupedPrompts.length === 0 ? (
          <div className='askTara__empty'>
            {t('NoIntegrationsFound') || 'No connected integrations found.'}
          </div>
        ) : (
          groupedPrompts.map((group) => (
            <IntegrationQuestionSection
              key={group.platformName}
              platformName={group.platformName}
              logo={group.logo}
              prompts={group.prompts}
              isExpanded={expandedSections.has(group.platformName)}
              onToggle={() => toggleSection(group.platformName)}
              onQuestionSelect={handleQuestionSelect}
              selectedKey={selectedKey}
              isSubmitting={isSubmitting}
            />
          ))
        )}
      </div>

      <OnboardingFooter />
    </div>
  );
};

export default AskTara;
